/**
 * Orbit Phase 6a / 6a.1 — MainLayout watcher: poll /api/orbit/jobs → sonner toasts.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useStudioUpload } from "@/components/layout/StudioUploadActions";
import { DEFAULT_STUDIO_ORG_KEY } from "@/lib/shoeStudioPrefs";
import { apiFetch } from "@/lib/apiFetch";
import {
  fetchOrbitJobs,
  orbitJobsQueryKey,
} from "./orbitApi";
import {
  claimOrbitToastSlot,
  dispatchOrbitOpen,
  isOrbitRunningStatus,
  orbitResultLines,
  orbitShowcaseDeepLink,
  pruneOrbitToastedKeys,
  styleOrbitLabel,
  type OrbitListJob,
} from "./orbitUi";

function toastDescription(job: OrbitListJob): string | undefined {
  const lines = orbitResultLines(job.result);
  if (lines.clean) return lines.primary;
  const parts = [
    lines.primary,
    lines.cutOffLine,
    lines.topTurnedLine,
  ].filter(Boolean);
  return parts.join(" · ") || undefined;
}

export function OrbitWatcher({
  orgKey = DEFAULT_STUDIO_ORG_KEY,
}: {
  orgKey?: string;
}) {
  const { hubStyles } = useStudioUpload();
  const seenRef = useRef<Map<string, string>>(new Map());
  const primedRef = useRef(false);

  const { data: jobs = [] } = useQuery({
    queryKey: orbitJobsQueryKey(orgKey),
    queryFn: () => fetchOrbitJobs(orgKey),
    refetchInterval: 15_000,
  });

  // F-6: prune toasted keys older than 7 days once on mount.
  useEffect(() => {
    pruneOrbitToastedKeys();
  }, []);

  useEffect(() => {
    if (!Array.isArray(jobs)) return;

    // First successful load: remember statuses, no toasts for already-finished.
    if (!primedRef.current) {
      for (const j of jobs) {
        seenRef.current.set(j.id, j.status);
      }
      primedRef.current = true;
      return;
    }

    for (const job of jobs) {
      const prev = seenRef.current.get(job.id);
      const next = job.status;
      seenRef.current.set(job.id, next);

      const wasRunning = prev != null && isOrbitRunningStatus(prev);
      if (!wasRunning) continue;

      if (next === "done") {
        if (!claimOrbitToastSlot(job.id)) continue;
        const label = styleOrbitLabel(hubStyles, job.style_id);
        const title = label ? `Orbit ready · ${label}` : "Orbit ready";
        toast(title, {
          id: `orbit-done-${job.id}`,
          description: toastDescription(job),
          duration: Infinity,
          action: {
            label: "Open",
            onClick: () => {
              const detail = dispatchOrbitOpen({
                jobId: job.id,
                styleId: job.style_id,
              });
              // Allow FootwearStudio listener to mark handled synchronously.
              queueMicrotask(() => {
                if (!detail.handled && job.style_id) {
                  window.location.assign(orbitShowcaseDeepLink(job.style_id));
                }
              });
            },
          },
        });
      } else if (next === "error") {
        if (!claimOrbitToastSlot(job.id)) continue;
        toast("Orbit couldn't be completed", {
          id: `orbit-err-${job.id}`,
          description: job.error || undefined,
          duration: Infinity,
          action: {
            label: "Retry",
            onClick: () => {
              if (!job.parent_job_id) return;
              void apiFetch("/api/orbit/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ anglesJobId: job.parent_job_id }),
              });
            },
          },
        });
      }
    }
  }, [jobs, hubStyles]);

  return null;
}
